import React, { useState, useMemo } from 'react';
import { Target, Users, TrendingUp, DollarSign } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = ['#4f46e5', '#8b5cf6', '#ec4899', '#f43f5e'];

export function AlumniAnnualFundModeler() {
  const [goalAmount, setGoalAmount] = useState<number>(500000);
  const [alumniBase, setAlumniBase] = useState<number>(15000);
  const [participationRate, setParticipationRate] = useState<number>(12);

  const model = useMemo(() => {
    const totalDonors = Math.floor(alumniBase * (participationRate / 100));
    
    // Standard Fundraising Gift Chart Math
    // Lead Gifts (Top 10 donors) ~ 40% of goal
    // Major Gifts (Next 50 donors) ~ 30% of goal
    // Special Gifts (Next 150 donors) ~ 15% of goal
    // General Gifts (Remaining) ~ 15% of goal
    
    const leadAmount = goalAmount * 0.40;
    const majorAmount = goalAmount * 0.30;
    const specialAmount = goalAmount * 0.15;
    const generalAmount = goalAmount * 0.15;

    const leadDonors = Math.min(10, Math.max(1, Math.floor(totalDonors * 0.01)));
    const majorDonors = Math.min(50, Math.max(1, Math.floor(totalDonors * 0.04)));
    const specialDonors = Math.min(150, Math.max(1, Math.floor(totalDonors * 0.15)));
    const generalDonors = Math.max(0, totalDonors - leadDonors - majorDonors - specialDonors);

    const tiers = [
      {
        name: 'Lead Gifts',
        donors: leadDonors,
        totalAmount: leadAmount,
        avgGift: leadDonors > 0 ? leadAmount / leadDonors : 0,
        color: COLORS[0]
      },
      {
        name: 'Major Gifts',
        donors: majorDonors,
        totalAmount: majorAmount,
        avgGift: majorDonors > 0 ? majorAmount / majorDonors : 0,
        color: COLORS[1]
      },
      {
        name: 'Special Gifts',
        donors: specialDonors,
        totalAmount: specialAmount,
        avgGift: specialDonors > 0 ? specialAmount / specialDonors : 0,
        color: COLORS[2]
      },
      {
        name: 'General Gifts',
        donors: generalDonors,
        totalAmount: generalAmount,
        avgGift: generalDonors > 0 ? generalAmount / generalDonors : 0,
        color: COLORS[3]
      }
    ];

    return { totalDonors, tiers };
  }, [goalAmount, alumniBase, participationRate]);

  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="grid md:grid-cols-3 gap-6">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 md:col-span-1 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              <Target className="w-4 h-4 mr-2 text-indigo-500" /> Target Goal ($)
            </label>
            <Input 
              type="number" 
              value={goalAmount} 
              onChange={(e) => setGoalAmount(Number(e.target.value))}
              className="text-lg font-bold"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              <Users className="w-4 h-4 mr-2 text-indigo-500" /> Total Alumni Base
            </label>
            <Input 
              type="number" 
              value={alumniBase} 
              onChange={(e) => setAlumniBase(Number(e.target.value))}
              className="text-lg font-bold"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
              <TrendingUp className="w-4 h-4 mr-2 text-indigo-500" /> Expected Participation (%)
            </label>
            <div className="flex items-center gap-4">
              <input 
                type="range" 
                min="1" 
                max="50" 
                value={participationRate} 
                onChange={(e) => setParticipationRate(Number(e.target.value))}
                className="flex-1 accent-indigo-600"
              />
              <span className="font-bold text-lg w-12 text-right">{participationRate}%</span>
            </div>
            <p className="text-xs text-slate-400 mt-2">National average is ~8-12%.</p>
          </div>
          
          <div className="pt-6 border-t border-slate-100 dark:border-slate-800">
            <div className="text-sm text-slate-500 mb-1">Required Donors</div>
            <div className="text-3xl font-black text-slate-900 dark:text-white">
              {model.totalDonors.toLocaleString()}
            </div>
            <div className="text-xs text-slate-400 mt-1">Based on base size and rate</div>
          </div>
        </Card>

        {/* Results */}
        <div className="md:col-span-2 space-y-6">
          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-lg font-bold mb-6 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-indigo-500" /> Gift Chart Breakdown
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="h-[250px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={model.tiers}
                      dataKey="totalAmount"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                    >
                      {model.tiers.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => formatCurrency(Number(value))}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend verticalAlign="bottom" height={36}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-4">
                {model.tiers.map((tier) => (
                  <div key={tier.name} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div>
                      <div className="font-bold text-sm" style={{ color: tier.color }}>{tier.name}</div>
                      <div className="text-xs text-slate-500">{tier.donors.toLocaleString()} donors required</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(tier.totalAmount)}</div>
                      <div className="text-xs text-slate-500">~{formatCurrency(tier.avgGift)} / ea</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6 border border-indigo-100 dark:border-indigo-800">
            <h4 className="font-bold text-indigo-900 dark:text-indigo-300 mb-2">Campaign Strategy Note</h4>
            <p className="text-sm text-indigo-700 dark:text-indigo-400">
              This model uses standard philanthropic tables where ~70% of funds are raised from the top ~5% of donors. 
              Ensure your Major Gifts officers are equipped to solicit the <strong>{model.tiers[0].donors + model.tiers[1].donors}</strong> individuals required for the Lead and Major tiers to make this campaign mathematically viable.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
